import type { LinkImportPreview } from '../api/linkImports';

const LINK_IMPORT_PREVIEW_KEY = 'omniscribe:link_import_preview';
const MAX_LINK_IMPORT_PREVIEWS = 10;

export interface CachedLinkImportPreview {
  url: string;
  preview: LinkImportPreview;
  cached_at: string;
}

interface CachedLinkImportPreviewList {
  items: CachedLinkImportPreview[];
}

export function saveLinkImportPreview(url: string, preview: LinkImportPreview) {
  try {
    const item: CachedLinkImportPreview = {
      url,
      preview,
      cached_at: new Date().toISOString(),
    };
    const dedupeKey = cacheDedupeKey(item);
    const items = loadLinkImportPreviews().filter((cached) => cacheDedupeKey(cached) !== dedupeKey);
    localStorage.setItem(
      LINK_IMPORT_PREVIEW_KEY,
      JSON.stringify({ items: [item, ...items].slice(0, MAX_LINK_IMPORT_PREVIEWS) }),
    );
  } catch {
    // Cache is best-effort.
  }
}

export function loadLinkImportPreview(): CachedLinkImportPreview | null {
  return loadLinkImportPreviews()[0] ?? null;
}

export function loadLinkImportPreviews(): CachedLinkImportPreview[] {
  try {
    const raw = localStorage.getItem(LINK_IMPORT_PREVIEW_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedLinkImportPreview | CachedLinkImportPreviewList;
    if ('items' in parsed && Array.isArray(parsed.items)) {
      return parsed.items.filter(isCachedLinkImportPreview).slice(0, MAX_LINK_IMPORT_PREVIEWS);
    }
    if (isCachedLinkImportPreview(parsed)) {
      return [parsed];
    }
    return [];
  } catch {
    return [];
  }
}

export function clearLinkImportPreview() {
  try {
    localStorage.removeItem(LINK_IMPORT_PREVIEW_KEY);
  } catch {
    // Cache is best-effort.
  }
}

function isCachedLinkImportPreview(value: unknown): value is CachedLinkImportPreview {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<CachedLinkImportPreview>;
  return typeof candidate.url === 'string' && !!candidate.preview && typeof candidate.cached_at === 'string';
}

function cacheDedupeKey(item: CachedLinkImportPreview): string {
  return item.preview.normalized_url || item.url;
}
